import { Injectable, isDevMode } from '@angular/core';
import { OperationVariables } from '@apollo/client/core';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { Apollo } from 'apollo-angular';
import { GraphQLFormattedError } from 'graphql';

import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { AuthService } from 'projects/box-lib/src/lib/services/auth-service.service';
import { Observable, catchError, tap, throwError, timer } from 'rxjs';

type EmptyObject = Record<string, any>;
type LegacyResultFields = {
  loading: boolean;
  errors?: ReadonlyArray<GraphQLFormattedError>;
};
type CompleteQueryResult<T> = Omit<Apollo.QueryResult<T>, 'data'> & LegacyResultFields & { data: T };
type CompleteMutationResult<T> = Omit<Apollo.MutateResult<T>, 'data'> & LegacyResultFields & { data: T };

// HotChocolate renvoie AUTH_NOT_AUTHENTICATED quand le jeton est absent ou expiré, AUTH_NOT_AUTHORIZED quand il est refusé
const AUTH_ERROR_CODES = ['AUTH_NOT_AUTHENTICATED', 'AUTH_NOT_AUTHORIZED'];

@Injectable({
  providedIn: 'root',
})
export class QueryManagerService {
  private isRedirectingToLogin = false;

  constructor(private authService: AuthService, private snackBarService: OuiSnackbarService, private apollo: Apollo) {}

  /**
   * This method add a side effect that display a snackbar with information on error to user and reset authent if error
   * is induced by authent ( code 401 or graphql error with code AUTH_NOT_AUTHORIZED)
   * error are not removed and will be transfered to caller
   *
   * @param {Observable<ApolloQueryResult<T>>} params - The observable that emits the Apollo query result.
   * @param {boolean} noSnackBar - (optional) If true, no snackbar will be displayed for graphql errors. (network errors will still be displayed)
   * @return {Observable<ApolloQueryResult<T>>} The observable that emits the Apollo query result with error handling.
   */
  handleErrors(params: Observable<any>, noSnackBar = false): Observable<any> {
    // in graph we can have error and 200 status. in that case error will be in response.errors array. So even if request succeed we check if there is an error in graphql
    // response.
    // we also test if request fail (network error). We use catch in pipe but we apply a catch an rethrow policy to allow sender to detect error and handle it at his level
    return params.pipe(
      //automatically display errors from response
      tap((response: any) => {
        const graphQLErrors = CombinedGraphQLErrors.is(response?.error)
          ? response.error.errors
          : (response?.errors ?? []);
        response.loading ??= false;
        response.errors ??= graphQLErrors.length > 0 ? graphQLErrors : undefined;
        if (graphQLErrors.length > 0) {
          if (
            graphQLErrors.some((error: GraphQLFormattedError) =>
              AUTH_ERROR_CODES.includes(error?.extensions?.['code'] as string)
            )
          ) {
            // at least one authentication error : the user token has expired, we need to reset authent
            this.onAuthentError();
            return;
          }

          if (!noSnackBar) {
            this.snackBarGraphQlErrors(graphQLErrors);
          }

          if (isDevMode()) {
            console.log('Received error from the server : ', response);
          }
        }
      }),
      catchError(error => {
        const networkError = error?.networkError ?? error;
        if (ServerError.is(networkError)) {
          if (networkError.statusCode === 401) {
            this.onAuthentError();
          } else if (networkError.statusCode === 500) {
            this.snackBarDefaultError();
          }
        }
        if (CombinedGraphQLErrors.is(error)) {
          this.snackBarGraphQlErrors(error.errors);
        }

        return throwError(() => error);
      })
    );
  }

  onAuthentError() {
    // several requests can fail at the same time : only one message and one redirection
    if (this.isRedirectingToLogin) {
      return;
    }
    this.isRedirectingToLogin = true;
    // display error message to inform user and reset authent (this will redirect user to login page)
    const message =
      "Votre session de connexion n'est plus valide. Vous allez être redirigé vers la page d'authentification.";
    this.snackBarService.open(message, 'error', 5000);
    timer(5000).subscribe(() => {
      this.authService.resetAuthent();
    });
  }

  public snackBarGraphQlError(error: any) {
    if (error.extensions && error.extensions.userMessage && typeof error.extensions.userMessage === 'string') {
      var message = error.extensions.userMessage as string;
      this.snackBarService.open(message, 'error', 5000);
    } else if (
      error.message &&
      typeof error.message === 'string' &&
      error.message.includes('The GraphQL request document contains more than 2048 fields')
    ) {
      this.snackBarService.open(
        "Vous avez initié une action qui consomment trop de données simultanément, ce qui engendre une instabilité de l'application. Merci de réduire le nombre d'opérations et de données associées pour poursuivre votre déclaration.",
        'error',
        10000
      );
    } else {
      this.snackBarDefaultError();
    }
  }

  public snackBarDefaultError() {
    const message =
      "Une erreur est survenue lors du traitement de votre requête. Veuillez rafraichir la page et contacter l'administrateur si l'erreur persiste.";
    this.snackBarService.open(message, 'error', 5000);
  }

  public snackBarGraphQlErrors(graphQLErrors: ReadonlyArray<GraphQLFormattedError>) {
    if (graphQLErrors.length > 0) {
      graphQLErrors.forEach(error => this.snackBarGraphQlError(error));
    }
  }

  /**
   * method to use request query to the server. wrap apollo query with default front behaviors
   *
   * @param options  : graphQL query options
   * @param noSnackBar  : (optionnal boolean) if true, no snackbar will be displayed for errors
   * @returns
   */
  public query<T, V extends OperationVariables = EmptyObject>(
    options: Apollo.QueryOptions<T, V>,
    noSnackBar = false,
    overGet = false
  ): Observable<CompleteQueryResult<T>> {
    options.errorPolicy = 'all';
    if (overGet) {
      if (options.context) {
        options.context['method'] = 'GET';
      } else {
        options.context = { method: 'GET' };
      }
    }
    var obs = this.apollo.query<T, V>(options);
    return this.handleErrors(obs, noSnackBar) as Observable<CompleteQueryResult<T>>;
  }

  /**
   * method to use to request mutate to the server. wrap apollo query with default front behaviors
   *
   * @param options  : graphQL query options
   * @param noSnackBar  : (optionnal boolean) if true, no snackbar will be displayed for errors
   * @returns
   */
  public mutate<T, V extends OperationVariables = EmptyObject>(
    options: Apollo.MutateOptions<T, V>,
    noSnackBar = false
  ): Observable<CompleteMutationResult<T>> {
    options.errorPolicy = 'all';
    var obs = this.apollo.mutate<T, V>(options);
    return this.handleErrors(obs, noSnackBar) as Observable<CompleteMutationResult<T>>;
  }
}
